import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { useSearch } from "@tanstack/solid-router";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@fleet/ui";
import { Button, buttonVariants } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { api, createApiQuery, SERVER_BASE_URL } from "@/lib/api";
import { SESSION_STALE_TIME_MS } from "@/lib/constants";
import { queryClient } from "@/lib/query-client";
import { queryKeys, sharedApi } from "@tendly/shared";
import { showToast } from "@/components/ui/toast";
import { Link2, ShieldCheck } from "@fleet/icons";
import { QueryErrorState } from "@/components/ui/query-error-state";

type ProviderId = "google" | "apple";

const PROVIDER_LABELS: Record<ProviderId, string> = {
  google: "Google",
  apple: "Apple",
};

const TRAILING_SLASH = /\/$/u;

/** URL of endpoint to initiate provider linking for the current user. */
function linkUrl(provider: ProviderId) {
  return `${SERVER_BASE_URL.replace(TRAILING_SLASH, "")}/api/auth/link-social/${provider}`;
}

/**
 * "Connected accounts": lets you link/unlink Google and Apple from the profile.
 * It only shows providers ENABLED in the backend. It respects the last-method
 * safeguard (the backend rejects leaving the user without access).
 */
export function ConnectedAccountsSection() {
  const { t } = useI18n();
  // Read ?linked=<provider> to show feedback after returning from the callback.
  const search = useSearch({ strict: false }) as () => { linked?: string };
  const [confirmUnlink, setConfirmUnlink] = createSignal<ProviderId | null>(null);
  const [busy, setBusy] = createSignal(false);

  // P23/CC-60 · phase 3 · P26. Both queries passed through DIRECT
  // `openapi-fetch` with `data ?? <default object>`, and `openapi-fetch` NEVER throws:
  // a 500 resolved to SUCCESS with FABRICATED data. Measured consequences with
  // browser on `main`:
  //
  //  · with `login-methods` down, the card said "Not connected" + "Connect"
  //    for a Google account that WAS linked, and `has_password`
  //    returned `false` — which determines whether the LAST
  //    access method can be unlinked;
  //  · the `QueryErrorState` of `connected-accounts-error` below was
  //    DEAD CODE: `isError` NEVER reached `true`. It was in the file,
  //    passed `tsc`, and was never rendered. Its presence was NOT the presence of
  //    arreglo.
  //
  // The `isSuccess` guard from I2 did not help here, because the status WAS `success`.
  // The failure must propagate from the SOURCE: `createApiQuery` goes through
  // `handleApiResponse`, which throws on any non-2xx.
  //
  // And note: the providers `queryKey` is shared with
  // `components/auth/social-auth-buttons.tsx`, which mounts FIRST. Fixing
  // this file alone DOES NOT close the defect — see the long note there.
  const providersQuery = createApiQuery(
    () => queryKeys.auth.socialProviders(),
    async () => sharedApi.socialProviders(api),
    () => ({ staleTime: SESSION_STALE_TIME_MS, retry: false }),
  );

  const methodsQuery = createApiQuery(
    () => queryKeys.auth.loginMethods(),
    async () => sharedApi.loginMethods(api),
    () => ({ retry: false }),
  );

  const enabledProviders = createMemo<ProviderId[]>(() => {
    const p = providersQuery.isSuccess ? providersQuery.data : undefined;
    if (!p) {
      return [];
    }
    const list: ProviderId[] = [];
    if (p.google) {
      list.push("google");
    }
    if (p.apple) {
      list.push("apple");
    }
    return list;
  });

  // P23/CC-60 I2: without the guard, with login-methods down the screen said
  // "Not connected" for an account that WAS linked, and hasPassword returned false
  // (which determines whether the LAST access method can be unlinked).
  const linkedProviders = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.providers ?? []) : [],
  );
  const hasPassword = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.has_password ?? false) : false,
  );

  /**
   * Did EITHER of the card's two reads fail?
   *
   * P23/CC-60 · phase 3 · P26 — the THIRD piece, and without it the other two do not
   * change the symptom. On error, `enabledProviders()` is `[]`, so the
   * `<Show>` wrapping the card hid it COMPLETELY: the error box remained
   * unreachable, just for a different reason. Measured: with
   * `/social/providers` returning 500, neither the section nor error appeared.
   *
   * The card must also exist when it could not be read, which is precisely
   * when the user needs to know that linked accounts cannot be determined
   * vinculadas.
   */
  const hasLoadFailure = () => providersQuery.isError || methodsQuery.isError;

  const retryAll = () => {
    void providersQuery.refetch();
    void methodsQuery.refetch();
  };

  const isLinked = (provider: ProviderId) => linkedProviders().includes(provider);

  // Total number of access methods (password + each linked social account).
  const totalMethods = createMemo(() => linkedProviders().length);

  // Is this the LAST method? (it cannot be unlinked without establishing another).
  const isLastMethod = (provider: ProviderId) =>
    isLinked(provider) && totalMethods() <= 1 && !hasPassword();

  const handleUnlink = async (provider: ProviderId) => {
    setBusy(true);
    try {
      const { error } = await sharedApi.unlinkAccount(api, provider);
      if (error) {
        const { message } = error;
        if (message.includes("only sign-in method")) {
          showToast(t(msg`Set a password before unlinking your only sign-in method.`), "error");
        } else {
          showToast(t(msg`Could not unlink the account. Please try again.`), "error");
        }
        return;
      }
      showToast(t(msg`Account unlinked.`), "success");
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    } finally {
      setBusy(false);
      setConfirmUnlink(null);
    }
  };

  // Feedback when returning from the linking flow (?linked=<provider>).
  let linkedFeedbackDone = false;
  createEffect(() => {
    const provider = search().linked;
    if (provider && !linkedFeedbackDone) {
      linkedFeedbackDone = true;
      showToast(t(msg`Account connected.`), "success");
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    }
  });

  return (
    <Show when={enabledProviders().length > 0 || hasLoadFailure()}>
      <Card>
        <CardHeader class="flex flex-row items-center gap-2 space-y-0">
          <ShieldCheck class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Connected accounts`)}</CardTitle>
        </CardHeader>
        <CardContent class="space-y-3">
          {/* P23/CC-60 I3: en error isLoading es false, asi que el guard de
              carga deja pasar y la pantalla pintaria el VACIO. Hay que ENVOLVER,
              no encadenar: encadenando quedarian error + esqueleto a la vez. */}
          <Show when={hasLoadFailure()}>
            <QueryErrorState
              testId="connected-accounts-error"
              compact
              title={t(msg`Couldn't load your linked accounts`)}
              onRetry={retryAll}
            />
          </Show>
          <Show when={!hasLoadFailure()}>
            <Show
              when={!methodsQuery.isPending}
              fallback={
                <div class="space-y-3">
                  <Skeleton class="h-14 w-full rounded-xl" />
                  <Skeleton class="h-14 w-full rounded-xl" />
                </div>
              }
            >
              <For each={enabledProviders()}>
                {(provider) => (
                  <div class="flex items-center gap-4 rounded-xl border border-border bg-muted/30 p-4">
                    <div class="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <Link2 class="h-5 w-5" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-semibold">{PROVIDER_LABELS[provider]}</p>
                      <Show
                        when={isLinked(provider)}
                        fallback={
                          <p class="text-xs text-muted-foreground">{t(msg`Not connected`)}</p>
                        }
                      >
                        <Badge
                          variant="secondary"
                          class="mt-1 h-5 border-none bg-primary/20 px-1.5 text-[10px] font-bold uppercase tracking-wider text-primary"
                        >
                          {t(msg`Connected`)}
                        </Badge>
                      </Show>
                    </div>

                    <Show
                      when={isLinked(provider)}
                      fallback={
                        <a
                          href={linkUrl(provider)}
                          class={buttonVariants({ variant: "outline", size: "sm" })}
                          data-testid={`link-${provider}`}
                        >
                          {t(msg`Connect`)}
                        </a>
                      }
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy() || isLastMethod(provider)}
                        title={
                          isLastMethod(provider)
                            ? t(msg`Set a password before unlinking your only sign-in method.`)
                            : undefined
                        }
                        onClick={() => setConfirmUnlink(provider)}
                        class="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        {t(msg`Disconnect`)}
                      </Button>
                    </Show>
                  </div>
                )}
              </For>

              <Show when={!hasPassword() && totalMethods() <= 1}>
                <p class="text-xs text-muted-foreground">
                  {t(
                    msg`This is your only sign-in method. Set a password to be able to disconnect it.`,
                  )}
                </p>
              </Show>
            </Show>
          </Show>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmUnlink() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmUnlink(null);
          }
        }}
        title={t(msg`Disconnect account?`)}
        description={t(
          msg`You will no longer be able to sign in with this provider. You can reconnect it anytime.`,
        )}
        confirmLabel={t(msg`Disconnect`)}
        cancelLabel={t(msg`Cancel`)}
        onConfirm={() => {
          const provider = confirmUnlink();
          if (provider) {
            void handleUnlink(provider);
          }
        }}
      />
    </Show>
  );
}
